import React from 'react';

class Alert {
  /**
   * Show confirmation dialog
   * @param {string} message - The confirmation message
   * @param {string} title - Optional title (default: "Confirm Action")
   * @returns {Promise<boolean>} - Returns true if confirmed, false if cancelled
   */
  static async confirm(message, title = "Confirm Action") {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
      
      const dialog = document.createElement('div');
      dialog.className = 'bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-md w-full mx-4 transform transition-all animate-fadeIn';
      
      dialog.innerHTML = `
        <div class="flex items-start gap-3 sm:gap-4">
          <div class="flex-shrink-0">
            <svg class="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <h3 class="text-base sm:text-lg font-semibold text-gray-900 mb-2 break-words">${title}</h3>
            <p class="text-sm text-gray-600 break-words">${message}</p>
          </div>
        </div>
        <div class="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4 sm:mt-6">
          <button id="confirm-btn" class="w-full sm:flex-1 bg-red-600 text-white px-4 py-2.5 sm:py-2 rounded-lg font-semibold hover:bg-red-700 transition text-sm sm:text-base order-2 sm:order-1">
            Confirm
          </button>
          <button id="cancel-btn" class="w-full sm:flex-1 bg-gray-200 text-gray-700 px-4 py-2.5 sm:py-2 rounded-lg font-semibold hover:bg-gray-300 transition text-sm sm:text-base order-1 sm:order-2">
            Cancel
          </button>
        </div>
      `;
      
      overlay.appendChild(dialog);
      document.body.appendChild(overlay);
      
      const cleanup = () => {
        overlay.remove();
      };
      
      dialog.querySelector('#confirm-btn').onclick = () => {
        cleanup();
        resolve(true);
      };
      
      dialog.querySelector('#cancel-btn').onclick = () => {
        cleanup();
        resolve(false);
      };
      
      overlay.onclick = (e) => {
        if (e.target === overlay) {
          cleanup();
          resolve(false);
        }
      };
    });
  }

  /**
   * Show success message
   * @param {string} message - The success message
   * @param {number} duration - Display duration in ms (default: 3000)
   */
  static success(message, duration = 3000) {
    this._showToast(message, 'success', duration);
  }

  /**
   * Show error message
   * @param {string} message - The error message
   * @param {number} duration - Display duration in ms (default: 4000)
   */
  static error(message, duration = 4000) {
    this._showToast(message, 'error', duration);
  }

  /**
   * Show info message
   * @param {string} message - The info message
   * @param {number} duration - Display duration in ms (default: 3000)
   */
  static info(message, duration = 3000) {
    this._showToast(message, 'info', duration);
  }

  /**
   * Internal method to show toast notifications
   */
  static _showToast(message, type, duration) {
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 left-4 sm:left-auto sm:right-4 z-50 transform transition-all duration-300 translate-x-0 animate-slideIn';
    
    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      info: 'bg-blue-500'
    };
    
    const icons = {
      success: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />`,
      error: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />`,
      info: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />`
    };
    
    toast.innerHTML = `
      <div class="${colors[type]} text-white px-4 py-3 sm:px-6 sm:py-4 rounded-lg shadow-lg flex items-center gap-2 sm:gap-3 max-w-md mx-auto sm:mx-0">
        <svg class="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          ${icons[type]}
        </svg>
        <p class="font-medium text-sm sm:text-base break-words flex-1">${message}</p>
      </div>
    `;
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.transform = 'translateX(400px)';
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, duration);
  }
}

// Add animations to document if not already present
if (typeof document !== 'undefined') {
  const styleId = 'alert-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: scale(0.95);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }
      @keyframes slideIn {
        from {
          transform: translateY(-100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }
      .animate-fadeIn {
        animation: fadeIn 0.2s ease-out;
      }
      .animate-slideIn {
        animation: slideIn 0.3s ease-out;
      }
    `;
    document.head.appendChild(style);
  }
}

export default Alert;